import type { WorkerSafetyEvent } from '@/domains/worker-mobile/model';

export type AlarmPopupLevel = 'info' | 'caution' | 'warning' | 'measure';

export type AlarmPopupSourceItem = {
  description: string;
  deviceName?: string;
  fieldUid?: string;
  id: string;
  imageUrl?: string | null;
  level: AlarmPopupLevel;
  locationName: string;
  occurredAt: string;
};

export type AlarmPopupMessage = {
  body?: unknown;
  event?: unknown;
  field_uid?: unknown;
  fieldUid?: unknown;
};

export type AlarmPopupItem = AlarmPopupSourceItem & {
  alarmUid: string;
  targetUid: string;
  deviceUid?: string | null;
  severityText: string;
  bodyMessage: string;
};

const UNKNOWN_EVENT_TEXT = 'UNKNOWN EVENT';

/**
 * 문자열 형태 값을 안전하게 정규화합니다.
 */
function readString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();
  return normalizedValue || null;
}

/**
 * UUID가 화면에 직접 노출되지 않도록 장비명 대체 문구로 치환합니다.
 */
function removeUidText(value: string | null | undefined, fallback = '장비'): string {
  const text = readString(value);
  if (!text) {
    return fallback;
  }

  return text.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, fallback);
}

/**
 * 알람 등급을 팝업 라벨로 변환합니다.
 */
function resolveSeverityText(level: AlarmPopupLevel): string {
  if (level === 'warning') return '경고';
  if (level === 'caution') return '주의';
  if (level === 'measure') return '계측';
  return '정보';
}

/**
 * 미정의 SACON 이벤트를 팝업 대상에서 제외합니다.
 */
function isUnknownEventAlarm(body: Record<string, unknown>, meta?: Record<string, unknown>): boolean {
  const alarmData = meta?.alarm_data as Record<string, unknown> | undefined;
  const detail = meta?.detail as Record<string, unknown> | undefined;
  const detailBody = detail?.body as Record<string, unknown> | undefined;
  const candidates = [
    body.event_message,
    body.eventMessage,
    body.sensor_state_name,
    body.sensorStateName,
    body.message,
    body.content,
    body.description,
    meta?.event_message,
    meta?.eventMessage,
    alarmData?.event_message,
    alarmData?.eventMessage,
    detail?.event_message,
    detail?.eventMessage,
    detailBody?.event_message,
    detailBody?.eventMessage,
  ];

  return candidates.some(value => readString(value)?.toUpperCase().includes(UNKNOWN_EVENT_TEXT));
}

/**
 * 로그인/회원가입/관리자 화면 여부를 판단해 알림 UI를 제외할지 결정합니다.
 */
export function isAlarmPopupDisabledRoute(pathname: string | null): boolean {
  return pathname === '/' || pathname === '/login' || pathname === '/signup' || pathname?.startsWith('/admin') === true;
}

/** 새 occurrence를 앞에 추가하고 중복 없이 최신 표시 한도를 유지합니다. */
export function prependAlarmPopupItem(queue: readonly AlarmPopupItem[], item: AlarmPopupItem, maxItems: number): AlarmPopupItem[] {
  if (queue.some(current => current.targetUid === item.targetUid)) return [...queue];
  return [item, ...queue].slice(0, maxItems);
}

/** 근로자 진입 알림은 저장된 장비 알람 확인 요청 없이 공용 팝업 큐에 표시합니다. */
export function buildWorkerSafetyAlarmPopupItem(event: WorkerSafetyEvent): AlarmPopupItem | null {
  if (event.transition !== 'ENTER') return null;
  const danger = event.severity === 'DANGER';
  const headline = `${event.user_name} · ${danger ? '위험' : '경고'} 구역 진입`;
  const zones = event.zones.map(zone => zone.name).join(', ');

  return {
    id: event.runtime_key,
    targetUid: event.runtime_key,
    alarmUid: '',
    fieldUid: event.field_uid,
    level: danger ? 'warning' : 'caution',
    severityText: danger ? '경고' : '주의',
    bodyMessage: headline,
    locationName: headline,
    description: `${zones}${event.reason === 'CONFIGURATION' ? ' · 구역 설정 변경 후 감지' : ''}`,
    occurredAt: event.occurred_at,
  };
}

/** 진입은 공통 정규화의 제목을 사용하고 다른 V2 알림은 확인된 수령인 이름을 앞에 표시합니다. */
function resolvePopupHeadline(body: Record<string, unknown>, headline: string, deviceName: string): string {
  if (body.provider !== 'V2') return headline;
  if (body.alarm_code === 'MOVEMENT_ENTRY' || body.alarm_code === 'MOVEMENT_EXIT') return headline;
  const recipientName = readString(body.notification_display_name);
  if (!recipientName) return headline;

  // 확인된 장비명 접두어만 제거하여 다른 이름의 일부나 사유 중간의 장비명은 보존합니다.
  if (headline.startsWith(deviceName)) {
    const suffix = headline.slice(deviceName.length);
    if (!suffix || /^(?:\s|[-:：])/.test(suffix)) headline = suffix.replace(/^\s*(?:[-:：]\s*)?/, '').trim();
  }
  if (headline.startsWith(recipientName)) {
    const suffix = headline.slice(recipientName.length);
    if (!suffix || /^(?:\s|[-:：])/.test(suffix)) return headline;
  }
  return headline ? `${recipientName} - ${headline}` : recipientName;
}

/**
 * 검증된 AlarmItem과 원본 메시지를 전역 팝업 항목으로 변환합니다.
 */
export function buildAlarmPopupItem(message: AlarmPopupMessage, item: AlarmPopupSourceItem): AlarmPopupItem | null {
  const body = message.body && typeof message.body === 'object' ? (message.body as Record<string, unknown>) : {};
  // 모델이 제외한 알림은 목록·이력에 남기고 팝업 큐와 알림음에서만 제외합니다.
  if (body.popup_enabled === false) return null;
  // 화면 진입·재조회로 복원한 정보 이력은 새 수신 팝업에서 제외하고 경고·주의 표시는 유지합니다.
  if (item.level === 'info' && body.is_initial_snapshot === true) {
    return null;
  }
  const meta = body.meta as Record<string, unknown> | undefined;
  if (isUnknownEventAlarm(body, meta)) {
    return null;
  }

  const alarmMessage = readString(body.message) ?? readString(body.content) ?? readString(body.alarm_message) ?? item.description ?? '알림이 수신되었습니다.';
  const fieldUid = readString(message.field_uid) ?? readString(message.fieldUid) ?? readString(body.field_uid) ?? readString(body.fieldUid) ?? item.fieldUid;
  const deviceUid =
    readString(body.device_uid) ??
    readString(body.deviceUid) ??
    readString(body.device_code) ??
    readString(meta?.device_uid) ??
    readString(meta?.deviceUid) ??
    readString(meta?.device_code);
  const fallbackDeviceName = readString(meta?.device_name) ?? readString(meta?.deviceName) ?? readString(body.device_name) ?? readString(body.deviceName) ?? '장비';

  return {
    ...item,
    alarmUid: readString(body.alarm_uid) ?? readString(body.alarmUid) ?? '',
    targetUid: item.id,
    fieldUid: fieldUid ?? undefined,
    deviceUid,
    severityText: resolveSeverityText(item.level),
    bodyMessage: removeUidText(resolvePopupHeadline(body, item.locationName ?? item.description ?? alarmMessage, fallbackDeviceName), fallbackDeviceName),
    occurredAt: item.occurredAt,
  };
}
